import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { money } from "@/lib/format";
import { Loader2, Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const CATS = [
  { k: "popcorn", label: "Popcorn" },
  { k: "cookie", label: "Cookies" },
  { k: "pecans", label: "Cinnamon Pecans" },
];
const STATUSES = ["available", "low_stock", "sold_out"];
const STATUS_LABEL = { available: "Available", low_stock: "Low Stock", sold_out: "Sold Out" };

const empty = (cat) => ({
  category: cat, name: "", description: "", image: "",
  sizes: cat === "cookie" ? [] : [{ label: "", price: 0 }],
  status: "available", hidden: false, featured: false, best_seller: false, seasonal: false, sort_order: 99,
});

export default function Products() {
  const [cat, setCat] = useState("popcorn");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/products", { params: { category: cat } }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [cat]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const body = { ...editing };
    body.sizes = (body.sizes || []).filter((s) => s.label).map((s) => ({ label: s.label, price: Number(s.price) }));
    try {
      if (editing.id) await api.put(`/admin/products/${editing.id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Product saved");
      setEditing(null);
      load();
    } catch (e) { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    load();
  };
  const quickStatus = async (p, status) => {
    await api.put(`/admin/products/${p.id}`, { ...p, status });
    load();
  };

  const field = "bg-input border-gold/25 text-cream rounded-xl";

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-serif text-3xl text-cream">Product Management</h1>
        <Button className="btn-gold rounded-full font-semibold" onClick={() => setEditing(empty(cat))} data-testid="add-product-btn"><Plus size={16} className="mr-1" /> Add Product</Button>
      </div>

      <div className="flex gap-2 mb-6">
        {CATS.map((c) => (
          <button key={c.k} onClick={() => setCat(c.k)} data-testid={`product-cat-${c.k}`}
            className={`px-4 py-2 rounded-full text-sm border ${cat === c.k ? "border-gold bg-gold/15 text-gold" : "border-gold/20 text-cream/60"}`}>{c.label}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gold" size={36} /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => (
            <div key={p.id} className="gold-border-card rounded-xl bg-card-surface overflow-hidden" data-testid={`admin-product-${p.id}`}>
              <div className="flex gap-3 p-3">
                <img src={p.image} alt={p.name} className="h-20 w-20 rounded-lg object-cover border border-gold/20" />
                <div className="flex-1 min-w-0">
                  <p className="text-cream font-semibold truncate">{p.name}</p>
                  {p.sizes?.length > 0 && <p className="text-gold font-mono text-xs mt-0.5">{money(p.sizes[0].price)}+</p>}
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {p.hidden && <span className="text-[9px] bg-cream/10 text-cream/60 px-1.5 py-0.5 rounded">HIDDEN</span>}
                    {p.best_seller && <span className="text-[9px] btn-gold px-1.5 py-0.5 rounded">BEST</span>}
                  </div>
                </div>
              </div>
              <div className="px-3 pb-3">
                <div className="flex gap-1 mb-2">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => quickStatus(p, s)} data-testid={`status-btn-${p.id}-${s}`}
                      className={`flex-1 text-[10px] py-1.5 rounded border ${p.status === s ? "border-gold bg-gold/15 text-gold" : "border-gold/15 text-cream/50"}`}>{STATUS_LABEL[s]}</button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1 bg-transparent border-gold/40 text-cream hover:bg-gold/10 rounded-lg" onClick={() => setEditing({ ...p, sizes: p.sizes?.length ? p.sizes : [] })} data-testid={`edit-product-${p.id}`}><Pencil size={13} className="mr-1" /> Edit</Button>
                  <Button size="sm" variant="outline" className="bg-transparent border-destructive/40 text-destructive hover:bg-destructive/10 rounded-lg" onClick={() => remove(p.id)} data-testid={`delete-product-${p.id}`}><Trash2 size={13} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-card-surface border-gold/30 text-cream max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-gold text-2xl">{editing?.id ? "Edit" : "Add"} Product</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><Label className="text-cream/70 text-sm">Category</Label>
                <select className={`w-full ${field} h-11 px-3`} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="edit-category">
                  {CATS.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
                </select>
              </div>
              <div><Label className="text-cream/70 text-sm">Name</Label><Input className={field} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-name" /></div>
              <div><Label className="text-cream/70 text-sm">Description</Label><Textarea className={field} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="edit-description" /></div>
              <div><Label className="text-cream/70 text-sm">Image URL</Label><Input className={field} value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} data-testid="edit-image" /></div>

              {editing.category !== "cookie" && (
                <div>
                  <Label className="text-cream/70 text-sm">Sizes & Prices</Label>
                  <div className="space-y-2 mt-1">
                    {(editing.sizes || []).map((s, i) => (
                      <div key={i} className="flex gap-2">
                        <Input className={field} placeholder="Label (e.g. 6 oz Classic)" value={s.label} onChange={(e) => { const sz = [...editing.sizes]; sz[i] = { ...sz[i], label: e.target.value }; setEditing({ ...editing, sizes: sz }); }} data-testid={`edit-size-label-${i}`} />
                        <Input className={`${field} w-24`} type="number" placeholder="$" value={s.price} onChange={(e) => { const sz = [...editing.sizes]; sz[i] = { ...sz[i], price: e.target.value }; setEditing({ ...editing, sizes: sz }); }} data-testid={`edit-size-price-${i}`} />
                        <button onClick={() => setEditing({ ...editing, sizes: editing.sizes.filter((_, x) => x !== i) })} className="text-cream/40 hover:text-destructive"><X size={16} /></button>
                      </div>
                    ))}
                    <Button size="sm" variant="outline" className="bg-transparent border-gold/40 text-gold rounded-lg" onClick={() => setEditing({ ...editing, sizes: [...(editing.sizes || []), { label: "", price: 0 }] })} data-testid="add-size-btn"><Plus size={13} className="mr-1" /> Add Size</Button>
                  </div>
                </div>
              )}

              <div><Label className="text-cream/70 text-sm">Status</Label>
                <select className={`w-full ${field} h-11 px-3`} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="edit-status">
                  {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
              </div>

              <div className="flex flex-wrap gap-4">
                {[["hidden", "Hidden"], ["featured", "Featured"], ["best_seller", "Best Seller"], ["seasonal", "Seasonal"]].map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 text-sm text-cream/80">
                    <input type="checkbox" checked={!!editing[k]} onChange={(e) => setEditing({ ...editing, [k]: e.target.checked })} data-testid={`edit-${k}`} /> {l}
                  </label>
                ))}
              </div>

              <Button className="btn-gold w-full rounded-full py-6 font-semibold" onClick={save} data-testid="save-product-btn">Save Product</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
